import { ArcElement, Chart as ChartJS, Legend, Tooltip } from 'chart.js';
import DoughnutLabel from 'chartjs-plugin-doughnutlabel-rebourne';
import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { Doughnut } from 'react-chartjs-2';
import { useSelector } from 'react-redux';
import { getApiStatus } from '../../../core/utils';
import datalabelsPlugin from 'chartjs-plugin-datalabels';
import DataTable from '../../../core/components/DataTable';
import Loader from '../../../core/components/Loader';
import moment from 'moment';
import { useAppContext } from '../../../providers/AppProvider';

ChartJS.register(ArcElement, Tooltip, Legend, DoughnutLabel, datalabelsPlugin);
interface Props {
  hourlyWashCounts: {
    [key: string]: {
      count: number;
      countByProduct: { [key: string]: number };
      countsBySiteId: { [key: number]: number };
    };
  };
  tunnelLength: number;
  fromDate: string;
  untilDate: string;
}
const OccupancyKpi = ({ hourlyWashCounts, tunnelLength, fromDate, untilDate }: Props) => {
  // state
  const [chartInfo, setChartInfo] = useState({ data: { datasets: [] }, options: {} });
  const [expectedHourlyCount, setExpectedHourlyCount] = useState(0);
  const [currentHourlyCount, setCurrentHourlyCount] = useState(0);
  const [occupancy, setOccupancy] = useState(0);
  // subscribes
  const loadingApiCall = useSelector((state) => getApiStatus(state, 'GET_OVERVIEW_CLAIMCOUNTS'));
  // hooks
  const intl = useIntl();
  const timeZone = useAppContext().timeZone;
  const handleCarCountByTunnelLength = () => {
    switch (tunnelLength) {
      case 55:
        return 75;
      case 90:
        return 135;
      case 110:
        return 160;
      case 130:
        return 180;
      default:
        return 1;
    }
  };

  const getCurrentHourlyCount = () => {
    if (hourlyWashCounts) {
      const currentHour = moment.tz(fromDate, timeZone).hour();
      const hourlyCount = hourlyWashCounts[currentHour]?.count;
      return hourlyCount || 0;
    }
    return 0;
  };

  useEffect(() => {
    createChartData();
  }, [currentHourlyCount, expectedHourlyCount, occupancy]);

  useEffect(() => {
    setCurrentHourlyCount(getCurrentHourlyCount());
    setExpectedHourlyCount(handleCarCountByTunnelLength());
  }, [tunnelLength, hourlyWashCounts, fromDate, untilDate]);

  useEffect(() => {
    setOccupancy((currentHourlyCount / expectedHourlyCount) * 100);
  }, [currentHourlyCount, expectedHourlyCount]);

  const calculateColor = (occ: number) => {
    if (occ >= 80) {
      return '#34C673';
    }
    if (occ < 80 && occ >= 50) {
      return '#29A4D4';
    }

    return 'rgba(255,0,0, 0.5)';
  };

  const handleDisplayTitle = () => {
    if (occupancy >= 100) {
      return intl.formatMessage({ id: 'overPerforming' });
    }
    if (occupancy >= 80) {
      return intl.formatMessage({ id: 'busy' });
    }
    if (occupancy < 80 && occupancy >= 65) {
      return intl.formatMessage({ id: 'good' });
    }
    if (occupancy < 65 && occupancy >= 20) {
      return intl.formatMessage({ id: 'underPerforming' });
    }

    return intl.formatMessage({ id: 'slow' });
  };

  const calculateTotal = () => {
    const difference = expectedHourlyCount - currentHourlyCount;
    if (difference <= 0) {
      return {
        actual: currentHourlyCount,
        remaining: null,
      };
    }
    return {
      actual: currentHourlyCount,
      remaining: expectedHourlyCount - currentHourlyCount,
    };
  };

  const createChartData = () => {
    const labels = ['Actual Washes', 'Remaining Washes'];
    const data: any = {
      datasets: [
        {
          label: intl.formatMessage({ id: 'hourlyOccupancy' }),
          data: [calculateTotal().actual, calculateTotal().remaining],
          dataLabels: {
            color: 'white',
          },
          percent: occupancy,
          backgroundColor: [calculateColor(occupancy), '#DDDDDD'],
          borderColor: [calculateColor(occupancy), '#DDDDDD'],
          borderRadius: 1,
          borderWidth: 0,
        },
      ],
      labels,
    };
    const options = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '75%',
      radius: '90%',
      plugins: {
        doughnutlabel: {
          labels: [
            {
              text: `${occupancy.toFixed(2)}%`,
              color: '#000000',
              font: {
                size: 20,
              },
            },
          ],
        },
        dataLabels: {
          display: true,
          align: 'center',
          anchor: 'center',
          color: 'white',
        },
        legend: {
          display: false,
          labels: {
            color: 'white',
          },
        },
        title: {
          display: true,
          text: `Status: ${handleDisplayTitle()}`,
          position: 'top',
          font: {
            size: 20,
          },
        },
      },
    };
    setChartInfo({ data, options });
  };

  const drawChart = () => {
    if (!loadingApiCall) {
      if (tunnelLength === null || tunnelLength === undefined) {
        return (
          <div className="chart-section-empty">{intl.formatMessage({ id: 'noTunnelLength' })}</div>
        );
      } else {
        return <Doughnut data={chartInfo.data} options={chartInfo.options} />;
      }
    }
    return <Loader />;
  };

  return (
    <div>
      <section className="container-fluid overview-chart-card">
        <div className="chart-title-section">
          <div className="chart-title">{intl.formatMessage({ id: 'hourlyOccupancy' })}</div>
        </div>
        <div className="chart-section" style={{ height: '20vh' }}>
          {drawChart()}
        </div>
      </section>
    </div>
  );
};

export default OccupancyKpi;
